<template>
  <HGuestLayout
    id="jb-bookings"
    :brand="org.org?.brandColor || NEUTRAL_BRAND"
    :name="org.orgName"
    :logo="org.logoUrl"
    :tagline="org.settings.footerTagline"
    product="Bookings"
    :embedded="isEmbedded"
    :class="{
      'is-embedded': isEmbedded,
      'has-embedded-actions': hasEmbeddedActions,
      'has-fixed-actions': hasFixedActions,
    }"
    :style="hasFixedActions && actionBarHeight ? { '--booking-action-bar-height': `${actionBarHeight}px` } : undefined"
  >
    <OrgSuspendedScreen v-if="suspension" />
    <router-view v-else />
  </HGuestLayout>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { HGuestLayout } from '@hospora/ui'
import { useOrgStore } from './stores/org'
import { NEUTRAL_BRAND } from './lib/brand'
import { suspension } from '@hospora-bookings/shared-ui'
import OrgSuspendedScreen from './components/OrgSuspendedScreen.vue'

const isEmbedded = typeof window !== 'undefined' && window.parent !== window
const hasEmbeddedActions = ref(false)
const parentOwnsActions = ref(false)
const ACTION_SELECTOR = '[data-booking-action]'
const ACTION_GROUP_SELECTOR = '[data-booking-action-group]'
let actionObserver = null
let lastActions = ''

// Standalone (not in an iframe) the page's own Back / Continue row is pinned to
// the bottom of the viewport, like the JB & Sons website does for the embed.
// Its height is measured so the page can reserve room for it.
const hasFixedActions = ref(false)
const actionBarHeight = ref(0)
let actionGroupObserver = null
let fixedActionGroup = null

function syncFixedActionBar() {
  if (isEmbedded) return
  const root = document.getElementById('jb-bookings')
  const group = Array.from(root?.querySelectorAll(ACTION_GROUP_SELECTOR) ?? [])
    .find((element) => element.querySelector(ACTION_SELECTOR)) ?? null
  hasFixedActions.value = Boolean(group)
  if (group === fixedActionGroup) return

  actionGroupObserver?.disconnect()
  fixedActionGroup = group
  actionBarHeight.value = 0
  if (group) actionGroupObserver?.observe(group)
}

// When the app is embedded in an iframe (e.g. in the JB website's Bookings page),
// report the document height to the parent so the iframe can auto-resize.
// The parent also needs to handle the 'jb-booking-scroll-top' postMessage event
// to scroll to the top of the page on step navigation (see router/index.ts).
let resizeObserver = null

function reportHeight() {
  if (isEmbedded) {
    window.parent.postMessage(
      { type: 'jb-booking-height', height: document.body.scrollHeight },
      '*'
    )
  }
}

function getActionLabel(element, action) {
  const label = (element.textContent || '').replace(/\s+/g, ' ').trim()
  return label || (action === 'back' ? 'Back' : 'Continue')
}

// The parent owns the fixed footer, but the iframe's native controls remain the
// source of truth for validation, loading state, and route navigation.
function reportActions() {
  syncFixedActionBar()
  if (!isEmbedded) return

  const actions = {}
  for (const element of document.querySelectorAll(ACTION_SELECTOR)) {
    const action = element.dataset.bookingAction
    if ((action !== 'back' && action !== 'continue') || actions[action]) continue

    actions[action] = {
      label: getActionLabel(element, action),
      disabled: element instanceof HTMLButtonElement
        ? element.disabled
        : element.getAttribute('aria-disabled') === 'true',
    }
  }

  const serialized = JSON.stringify(actions)
  hasEmbeddedActions.value = parentOwnsActions.value && Boolean(actions.back || actions.continue)
  if (serialized === lastActions) return

  lastActions = serialized
  window.parent.postMessage({ type: 'jb-booking-actions', actions }, '*')
}

function handleParentMessage(event) {
  if (!isEmbedded || event.source !== window.parent || !event.data) return

  if (event.data.type === 'jb-booking-actions-request') {
    parentOwnsActions.value = true
    reportActions()
    return
  }

  if (event.data.type !== 'jb-booking-action') return
  const action = event.data.action
  if (action !== 'back' && action !== 'continue') return

  const target = Array.from(document.querySelectorAll(ACTION_SELECTOR))
    .find((element) => element.dataset.bookingAction === action)
  if (target instanceof HTMLElement) target.click()
}

// The organisation (name, colour, venues, copy) comes from the API once, for every page.
const org = useOrgStore()
const route = useRoute()
watch(() => route.fullPath, reportActions, { flush: 'post' })

onMounted(() => {
  void org.load()
  reportHeight()
  resizeObserver = new ResizeObserver(reportHeight)
  resizeObserver.observe(document.body)
  if (!isEmbedded) {
    actionGroupObserver = new ResizeObserver(([entry]) => {
      if (entry) actionBarHeight.value = Math.ceil(entry.target.getBoundingClientRect().height)
    })
  }
  reportActions()

  actionObserver = new MutationObserver(reportActions)
  actionObserver.observe(document.getElementById('jb-bookings'), {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled', 'data-booking-action'],
  })
  window.addEventListener('message', handleParentMessage)
})

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect()
  if (actionObserver) actionObserver.disconnect()
  if (actionGroupObserver) actionGroupObserver.disconnect()
  window.removeEventListener('message', handleParentMessage)
})
</script>

<style>
/* Global resets — scoped to this app's container so they don't pollute the host page */
#jb-bookings *,
#jb-bookings *::before,
#jb-bookings *::after {
  box-sizing: border-box;
}

#jb-bookings {
  font-family: var(--h-font-body);
  line-height: 1.6;
  font-size: 16px;
  color: var(--h-ink);
  background-color: var(--booking-page-background, var(--h-bg));
}

/* The parent page renders the controls in its own viewport, so they remain
   visible even though this iframe is sized to the full booking document. */
#jb-bookings.is-embedded.has-embedded-actions {
  padding-bottom: 112px;
}

/* Visually hidden, still in the DOM (the buttons stay the source of truth). Kept on purpose: !important so no view's
   own layout rule can bring the hidden group back. */
#jb-bookings.is-embedded.has-embedded-actions [data-booking-action-group] {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  margin: -1px !important;
  padding: 0 !important;
  overflow: hidden !important;
  clip: rect(0 0 0 0) !important;
  clip-path: inset(50%) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

#jb-bookings.is-embedded.has-embedded-actions .spw-back-link {
  display: none;
}

/* Standalone: the same fixed bottom bar the JB & Sons website shows around the
   embed, applied to the page's own controls so validation and clicks are unchanged. */
#jb-bookings.has-fixed-actions {
  padding-bottom: calc(var(--booking-action-bar-height, 96px) + 16px);
}

#jb-bookings.has-fixed-actions [data-booking-action-group] {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 100;
  width: auto;
  max-width: none;
  margin: 0;
  padding: 12px max(16px, calc((100% - 1100px) / 2)) calc(12px + env(safe-area-inset-bottom));
  background: color-mix(in srgb, var(--h-bg) 96%, transparent);
  border-top: 1px solid var(--h-accent-soft);
  box-shadow: 0 -6px 24px color-mix(in srgb, var(--h-ink) 12%, transparent);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Kept on purpose: views give their action buttons a top margin that the pinned bar must not inherit. */
#jb-bookings.has-fixed-actions [data-booking-action] {
  margin-top: 0 !important;
}

@media (max-width: 600px) {
  #jb-bookings.has-fixed-actions [data-booking-action-group] {
    gap: 8px;
  }

  #jb-bookings.has-fixed-actions [data-booking-action] {
    flex: 1 1 0;
    min-width: 0;
    padding-inline: 12px;
    font-size: 16px;
    white-space: normal;
  }
}

#jb-bookings .h-guest__content a { color: inherit; }
#jb-bookings button { font-family: inherit; }
#jb-bookings img { border-radius: var(--h-radius-md); }
#jb-bookings h1,
#jb-bookings h2,
#jb-bookings h3,
#jb-bookings h4,
#jb-bookings h5,
#jb-bookings h6 {
   font-family: var(--h-font-display);
  color: var(--h-ink);
  margin: 0 0 1rem;
}

/* Hero headings inside coloured containers inherit their parent's color
   rather than the global brand colour — covers manage-hero, confirm-hero, etc. */
#jb-bookings .manage-hero h1,
#jb-bookings .manage-hero h2,
#jb-bookings .confirm-hero h1,
#jb-bookings .confirm-hero h2 {
  color: inherit;
}
</style>
